import { defineStore } from 'pinia'
import { ref } from 'vue'
import { chatNamesMatch } from '@shared/index'
import { fetchChatMutes, postChatMute, type ChatMuteResult } from '../api/chatMutes'

/**
 * Usernames whose chat lines this account does not see.
 * The list follows the account. Other players still see those lines.
 */
export const useChatMuteStore = defineStore('chatMutes', () => {
  const names = ref<string[]>([])
  const ready = ref(false)
  let generation = 0

  function isMuted(username: string): boolean {
    return names.value.some((name) => chatNamesMatch(name, username))
  }

  function remember(username: string, muted: boolean): void {
    const rest = names.value.filter((name) => !chatNamesMatch(name, username))
    names.value = muted ? [...rest, username] : rest
  }

  /** Keeps a mute when that duelist changes their public name. */
  function applyRename(previous: string, username: string): void {
    let changed = false
    const next = names.value.map((name) => {
      if (!chatNamesMatch(name, previous)) return name
      changed = true
      return username
    })
    if (changed) names.value = next
  }

  async function load(token: string): Promise<void> {
    const ticket = ++generation
    try {
      const list = await fetchChatMutes(token)
      if (ticket !== generation) return
      names.value = Array.isArray(list.mutes) ? list.mutes : []
      ready.value = true
    } catch {
      if (ticket !== generation) return
      ready.value = true
    }
  }

  async function setMuted(
    token: string,
    username: string,
    muted: boolean,
  ): Promise<ChatMuteResult> {
    const result = await postChatMute(token, username, muted)
    remember(result.username, result.muted)
    return result
  }

  function clear(): void {
    generation += 1
    names.value = []
    ready.value = false
  }

  return {
    names,
    ready,
    isMuted,
    applyRename,
    load,
    setMuted,
    clear,
  }
})
